import {
  ensureCablesRuntime,
  observeProximity,
  runAfterWebflowInteractions,
} from "../shared/cables-runtime";

const ROBOT_PATCH_URL =
  "https://d2my2wpsc41l6t.cloudfront.net/modernexpo/js/ModernExpo_-_Robot_Scroller.gz.json";

export function initRobotAnimation({
  track = document.querySelector(".content__track"),
  canvas = document.querySelector(".robot-anim"),
} = {}) {
  const gsapInstance = window.gsap;
  const scrollTriggerPlugin = window.ScrollTrigger;

  if (
    !track ||
    !canvas ||
    !window.CABLES?.Patch ||
    !gsapInstance?.utils ||
    !scrollTriggerPlugin?.create ||
    canvas.dataset.robotAnimationInitialized === "true"
  ) {
    return null;
  }

  const patch = new window.CABLES.Patch({
    patchFile: ROBOT_PATCH_URL,
    glCanvasId: canvas.id,
    glCanvasResizeToWindow: true,
    canvas: { alpha: false, premultipliedAlpha: false },
  });

  const scrollTrigger = scrollTriggerPlugin.create({
    trigger: track,
    start: "top bottom",
    end: "bottom top",
    scrub: 1,
    invalidateOnRefresh: true,
    onUpdate: (self) => {
      const progress = gsapInstance.utils.mapRange(0, 1, -6, 10, self.progress);
      patch.setVarValue("progress", progress);
    },
  });

  scrollTrigger.disable(false);

  const pushInitialProgress = () => {
    if (typeof scrollTrigger.vars?.onUpdate === "function") {
      scrollTrigger.vars.onUpdate(scrollTrigger);
    }
  };

  observeProximity(
    track,
    () => {
      scrollTrigger.enable();
      scrollTrigger.refresh();
      pushInitialProgress();
    },
    () => scrollTrigger.disable(false)
  );

  canvas.dataset.robotAnimationInitialized = "true";
  return { patch, scrollTrigger };
}

function initRobotWhenNear(track, canvas) {
  const startRobot = () => {
    ensureCablesRuntime()
      .then(() => {
        initRobotAnimation({ track, canvas });
        window.ScrollTrigger?.refresh?.();
      })
      .catch((error) => {
        delete canvas.dataset.robotAnimationQueued;
        console.error("[CABLES] Failed to initialize robot", error);
      });
  };

  if (typeof window.IntersectionObserver !== "function") {
    startRobot();
    return;
  }

  const margin = window.innerWidth >= 1024 ? "100% 0px" : "20% 0px";
  const observer = new window.IntersectionObserver(
    (entries) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      startRobot();
    },
    {
      rootMargin: margin,
      threshold: 0,
    }
  );

  observer.observe(track);
}

export function bootRobotAnimation() {
  const track = document.querySelector(".content__track");
  const canvas = document.querySelector(".robot-anim");

  if (
    !track ||
    !canvas ||
    !window.gsap ||
    !window.ScrollTrigger ||
    canvas.dataset.robotAnimationQueued === "true"
  ) {
    return;
  }

  canvas.dataset.robotAnimationQueued = "true";
  window.gsap.registerPlugin(window.ScrollTrigger);

  runAfterWebflowInteractions(() => {
    if (window.innerWidth >= 1024) {
      initRobotWhenNear(track, canvas);
      return;
    }

    const deferRobot = () => window.setTimeout(() => initRobotWhenNear(track, canvas), 3000);

    if (document.readyState === "complete") {
      deferRobot();
    } else {
      window.addEventListener("load", deferRobot, { once: true });
    }
  });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bootRobotAnimation, { once: true });
} else {
  bootRobotAnimation();
}
